import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CaretDown, List, X, Buildings, HeartStraight, ArrowRight } from "@phosphor-icons/react";
import { useLang } from "../lib/i18n";
import { COMPANY, ATIKXZIO_SITE_URL } from "../lib/constants";
import { InstallApp } from "./InstallApp";
import { FOUNDING } from "../config/pricing";

const DARK_HERO_ROUTES = ["/", "/pricing", "/about", "/contact"];

const STR = {
  en: {
    products: "Products", pricing: "Pricing", about: "About", careers: "Careers", contact: "Contact",
    demo: "Book a demo", login: "Login", loginHint: "to Atikxzio",
    hmsSub: "Our flagship hospital management system — OPD, wards, billing and the owner's dashboard in one place.",
    explore: "Explore Atikxzio HMS", seePricing: "See pricing",
    cmh: "Atikxzio CMH · Career & wellbeing", soon: "Launching soon", dev: "Launching soon",
    announce: `Founding hospitals: ${FOUNDING.seats} seats, launch pricing locked`,
    apply: "Apply", dismiss: "Dismiss",
  },
  hi: {
    products: "प्रोडक्ट्स", pricing: "प्राइसिंग", about: "हमारे बारे में", careers: "करियर", contact: "संपर्क",
    demo: "डेमो बुक करें", login: "लॉगिन", loginHint: "Atikxzio पर",
    hmsSub: "हमारा प्रमुख हॉस्पिटल मैनेजमेंट सिस्टम — OPD, वार्ड, बिलिंग और ओनर डैशबोर्ड एक जगह।",
    explore: "Atikxzio HMS देखें", seePricing: "प्राइसिंग देखें",
    cmh: "Atikxzio CMH · करियर व मानसिक स्वास्थ्य", soon: "जल्द लॉन्च", dev: "जल्द लॉन्च",
    announce: `फ़ाउंडिंग अस्पताल: ${FOUNDING.seats} सीटें, लॉन्च प्राइसिंग तय`,
    apply: "आवेदन करें", dismiss: "बंद करें",
  },
};

const Logo = ({ onDark }) => (
  <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo">
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105 ${onDark ? "bg-white/10 ring-1 ring-white/25" : "bg-white ring-1 ring-navy/10"}`}>
      <img src={onDark ? "/brand/logo-mark-white.png" : "/logo-mark.png"} alt="Priyajit Systems" className="h-7 w-auto" />
    </span>
    <span className="leading-none">
      <span className={`block font-display text-[15px] font-black tracking-tight ${onDark ? "text-white" : "text-navy"}`}>
        PRIYAJIT <span className={onDark ? "text-electric-light" : "text-electric-ink"}>SYSTEMS</span>
      </span>
      <span className={`mt-1 block text-[8px] font-bold uppercase tracking-[0.3em] ${onDark ? "text-white/50" : "text-slate-500"}`}>Private Limited</span>
    </span>
  </Link>
);

const LangToggle = ({ lang, setLang, onDark }) => (
  <div className={`flex items-center rounded-full border p-0.5 ${onDark ? "border-white/20 bg-white/10" : "border-navy/10 bg-slate-50"}`} data-testid="lang-toggle" role="group" aria-label="Language">
    {[["en", "EN"], ["hi", "हिं"]].map(([k, label]) => (
      <button
        key={k}
        onClick={() => setLang(k)}
        className={`rounded-full px-2.5 py-1.5 text-xs font-black transition-colors duration-200 ${
          lang === k ? (onDark ? "bg-white text-navy" : "bg-navy text-white") : onDark ? "text-white/70 hover:text-white" : "text-slate-500 hover:text-navy"
        }`}
        data-testid={`lang-toggle-${k}`}
      >
        {label}
      </button>
    ))}
  </div>
);

export const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [announce, setAnnounce] = useState(true);
  const { pathname } = useLocation();
  const { lang, setLang } = useLang();
  const s = STR[lang];

  const darkHero = DARK_HERO_ROUTES.includes(pathname);
  const onDark = darkHero && !scrolled && !mobileOpen;

  useEffect(() => { setMobileOpen(false); setMenuOpen(false); }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLinks = [
    ["/pricing", s.pricing],
    ["/about", s.about],
    ["/careers", s.careers],
    ["/contact", s.contact],
  ];

  const linkCls = (p) =>
    `text-sm font-semibold transition-colors duration-200 ${
      onDark ? (pathname === p ? "text-white" : "text-white/75 hover:text-white")
             : (pathname === p ? "text-electric-ink" : "text-navy/80 hover:text-electric-ink")
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="navbar">
      {announce && (
        <div className="bg-electric-ink text-white" data-testid="announce-bar">
          <div className="mx-auto flex max-w-[1200px] items-center justify-center gap-3 px-5 py-1.5 lg:px-8">
            <Link to="/pricing#founding" className="flex items-center gap-2 text-center text-[11px] font-bold tracking-wide text-white transition-opacity duration-200 hover:opacity-85" data-testid="announce-bar-link">
              <span className="line-clamp-1">{s.announce}</span>
              <span className="hidden shrink-0 items-center gap-1 underline underline-offset-2 sm:inline-flex">{s.apply} <ArrowRight size={11} weight="bold" /></span>
            </Link>
            <button onClick={() => setAnnounce(false)} aria-label={s.dismiss} className="shrink-0 text-white/70 transition-colors duration-200 hover:text-white" data-testid="announce-bar-dismiss">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className={`transition-all duration-300 ${
        onDark ? "border-b border-white/10 bg-transparent py-3"
               : `border-b border-navy/[0.07] bg-white/90 backdrop-blur-xl ${scrolled ? "py-1.5 shadow-[0_4px_20px_rgba(11,30,77,0.07)]" : "py-3"}`
      }`}>
        <nav className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-5 lg:px-8">
          <Logo onDark={onDark} />

          <div className="hidden items-center gap-7 lg:flex">
            <div className="relative" onMouseEnter={() => setMenuOpen(true)} onMouseLeave={() => setMenuOpen(false)}>
              <button className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-200 ${onDark ? "text-white/75 hover:text-white" : "text-navy/80 hover:text-electric-ink"}`} data-testid="nav-products">
                {s.products} <CaretDown size={13} className={`transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} />
              </button>
              {menuOpen && (
                <div className="absolute left-1/2 top-full w-[27rem] -translate-x-1/2 pt-4" data-testid="nav-products-menu">
                  <div className="rounded-2xl border border-navy/[0.07] bg-white p-3 shadow-[0_16px_48px_rgba(11,30,77,0.16)]">
                    <div className="rounded-xl border border-navy/10 bg-mist p-5" data-testid="nav-dropdown-atikxzio-hms">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-electric-ink text-white"><Buildings size={21} weight="duotone" /></span>
                      <p className="mt-3 font-display text-base font-bold text-navy">Atikxzio HMS</p>
                      <p className="mt-1.5 text-xs leading-relaxed text-ink">{s.hmsSub}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-4">
                        <a href={ATIKXZIO_SITE_URL} target="_top" rel="noopener" className="inline-flex items-center gap-1.5 text-xs font-bold text-electric-ink" data-testid="nav-dropdown-hms-link">{s.explore} <ArrowRight size={13} /></a>
                        <Link to="/pricing" className="inline-flex items-center gap-1.5 text-xs font-bold text-navy/70 hover:text-navy">{s.seePricing} <ArrowRight size={13} /></Link>
                      </div>
                    </div>
                    <Link to="/products/atikxzio-cmh" className="mt-2 flex items-center justify-between rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-slate-50" data-testid="nav-dropdown-atikxzio-cmh">
                      <span className="flex items-center gap-2.5 text-sm font-semibold text-navy">
                        <HeartStraight size={18} weight="duotone" className="text-teal-ink" /> {s.cmh}
                      </span>
                      <span className="rounded-full bg-amberx-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amberx">{s.soon}</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {navLinks.map(([to, label]) => (
              <Link key={to} to={to} className={linkCls(to)} data-testid={`nav-${to.slice(1)}`}>{label}</Link>
            ))}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <LangToggle lang={lang} setLang={setLang} onDark={onDark} />
            <a href={COMPANY.loginUrl} target="_top" rel="noopener" className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${onDark ? "text-white/80 hover:text-white" : "text-navy/80 hover:text-electric-ink"}`} data-testid="nav-login">{s.login}</a>
            <Link to="/contact" className="rounded-full bg-electric-ink px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#0F4FA3]" data-testid="nav-book-demo">{s.demo}</Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LangToggle lang={lang} setLang={setLang} onDark={onDark} />
            <button className={onDark ? "text-white" : "text-navy"} onClick={() => setMobileOpen(true)} data-testid="nav-mobile-toggle" aria-label="Menu">
              <List size={26} />
            </button>
          </div>
        </nav>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex items-center justify-between border-b border-navy/[0.07] px-5 py-3">
            <Logo />
            <button onClick={() => setMobileOpen(false)} aria-label="Close" data-testid="nav-mobile-close" className="text-navy"><X size={26} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <p className="px-3 pt-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{s.products}</p>
            <a href={ATIKXZIO_SITE_URL} target="_top" rel="noopener" className="mt-2 block rounded-xl border border-navy/10 bg-mist p-4" data-testid="nav-mobile-atikxzio-hms">
              <span className="font-display text-sm font-bold text-navy">Atikxzio HMS</span>
              <span className="mt-1 block text-xs text-ink">{s.hmsSub}</span>
            </a>
            <Link to="/products/atikxzio-cmh" className="mt-2 flex items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold text-navy" data-testid="nav-mobile-atikxzio-cmh">
              Atikxzio CMH
              <span className="rounded-full bg-amberx-soft px-2 py-0.5 text-[10px] font-bold uppercase text-amberx">{s.dev}</span>
            </Link>
            <div className="mt-4 border-t border-navy/[0.07] pt-3">
              {navLinks.map(([to, label]) => (
                <Link key={to} to={to} className="block rounded-xl px-3 py-3 font-semibold text-navy">{label}</Link>
              ))}
              <a href={COMPANY.loginUrl} target="_top" rel="noopener" className="flex items-baseline gap-2 rounded-xl px-3 py-3 font-semibold text-navy" data-testid="nav-mobile-login">
                {s.login} <span className="text-xs font-normal text-slate-500">{s.loginHint}</span>
              </a>
              <InstallApp variant="menu" />
            </div>
          </div>
          <div className="border-t border-navy/[0.07] p-4 pb-[calc(5.75rem+env(safe-area-inset-bottom))]">
            <Link to="/contact" className="block rounded-full bg-electric-ink px-5 py-4 text-center font-bold text-white" data-testid="nav-mobile-book-demo">{s.demo}</Link>
          </div>
        </div>
      )}
    </header>
  );
};
